<script setup lang="ts">
import type { Agent } from '~/core/entities/agent/Agent';
import type { PropertyPricesProps } from '.';

import { PropertyContactFormModal } from '~/components/Property/PropertyContactFormModal';
import { PropertyPricesSkeleton } from '.';

import { cn } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { getAgentFirstName, getAgentName } from '~~/utils';
import propertyPricesLocale from './PropertyPrices.locale.json';

const props = withDefaults(defineProps<PropertyPricesProps>(), {
	askingPrice: null,
	condoName: null,
	condoFee: null,
	tax: null,
	priceClasses: '',
	launchYear: ''
});

const { t } = useI18n({
	useScope: 'local',
	messages: propertyPricesLocale
});

interface TableItem {
	label: string;
	value: string;
	key: keyof PropertyPricesProps;
}

const formattedPrice = computed<string | null>(() =>
	props.askingPrice
		? floatToLocaleString(props.askingPrice, {
				style: 'currency',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
				roundingMode: 'ceil'
			})
		: null
);

const tableKeys: (keyof PropertyPricesProps)[] = [
	'condoFee',
	'tax',
	'launchYear'
];

const pricingTableKeys: (keyof PropertyPricesProps)[] = ['condoFee', 'tax'];

const table = computed<TableItem[]>(() =>
	tableKeys.reduce((table, key) => {
		const value = props[key];

		if (typeof value === 'string' && value) {
			const item = {
				label: t(key),
				value: value,
				key
			};

			table.push(item);
		}

		if (typeof value === 'number' && value) {
			const formattingOptions: Intl.NumberFormatOptions =
				pricingTableKeys.includes(key)
					? {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
							roundingMode: 'ceil'
						}
					: {};

			const item = {
				label: t(key),
				value: floatToLocaleString(value, {
					style: 'currency',
					...formattingOptions
				}),
				key
			};

			table.push(item);
		}

		return table;
	}, [] as TableItem[])
);

const agentName = computed(() => getAgentName(props.agent));
const firstName = computed(() => getAgentFirstName(props.agent));
const shouldShowPropertyContactModal = computed(
	() =>
		props.agent &&
		props.companyName &&
		props.propertyType &&
		props.propertyId &&
		props.commercialId &&
		props.region
);

const isContactModalOpen = ref(false);

const { trackFormPropertyClicked } = useEvents();
const { loggedIn, user } = useUserSession();

function openContactModal() {
	if (!props.agent || !props.commercialId) return;

	isContactModalOpen.value = true;

	trackFormPropertyClicked({
		user_name: user.value?.name,
		user_email: user.value?.email,
		user_status: loggedIn.value ? 'logged in' : 'logged out',
		agent_name: props.agent.name,
		company_name: props.companyName,
		commercial_id: props.commercialId,
		asking_price: props.askingPrice,
		cta_text: t('broker.button'),
		section_name: props.sectionName || 'card (property details)',
		current_page:
			props.sectionName === 'card (property details)'
				? 'Detalhe do Imóvel'
				: 'PDP Form'
	});
}

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(
		props.agent?.profilePicture.croppedUrl || '',
		{
			format: 'webp',
			width: 100
		},
		{
			provider: 'AWSServerlessImageHandler'
		}
	)
);
</script>

<template>
	<PropertyPricesSkeleton v-if="isLoading" :is-loading="true" />

	<div v-else class="size-full space-y-8 bg-secondary px-4 py-5 md:p-8">
		<div v-if="table.length || formattedPrice || launchYear" class="space-y-5">
			<div v-if="formattedPrice">
				<p class="text-sm font-normal leading-normal text-primary-light-1">
					{{ t('askingPrice') }}
				</p>
				<p
					:class="
						cn(
							'font-matter-sq text-2xl font-normal leading-normal tracking-[-0.48px] text-primary md:text-[28px]',
							priceClasses
						)
					"
					data-testid="asking-price"
				>
					{{ formattedPrice }}
				</p>
			</div>
			<div>
				<div
					v-for="item in table"
					:key="`property-prices-${item.key}`"
					class="flex items-center justify-between border-b border-secondary-dark-1 py-2 text-sm font-normal leading-normal text-primary-light-3 first:pt-0 last:border-none last:pb-0"
					data-testid="item"
				>
					<p>{{ item.label }}</p>
					<p
						:class="{
							'underline decoration-[rgba(0,0,0,0.28)] underline-offset-[6px]':
								item.key === 'condoName'
						}"
					>
						{{ item.value }}
					</p>
				</div>
			</div>
		</div>
		<div class="space-y-4">
			<BrokerAvatarInfo
				:border="false"
				:show-company-avatar="true"
				:info-classes="{
					wrapper: 'max-w-[80%] md:max-w-[90%] lg:!max-w-[80%]',
					brokerName:
						'text-primary font-matter-sq text-base font-normal leading-normal tracking-[-0.32px]',
					companyName:
						'text-primary-light-2 font-normal text-xs leading-normal text-primary-light-2'
				}"
				:broker-src="brokerSrc || '/images/fallbacks/agent-avatar.webp'"
				:company-src="companyLogo ?? '/images/fallbacks/company-avatar.webp'"
				avatar-class="size-11"
				class="gap-4"
				:broker-name="agentName"
				:company-name="companyName"
			>
				<template #broker>{{ agentName }}</template>
				<template #company>{{ companyName }}</template>
			</BrokerAvatarInfo>

			<Button size="sm" class="h-11" @click="openContactModal">
				{{ t('broker.button') }} {{ firstName }}
			</Button>
		</div>

		<PropertyContactFormModal
			v-if="shouldShowPropertyContactModal"
			v-model="isContactModalOpen"
			:broker="agent || ({} as Agent)"
			:company-name="companyName || ''"
			:property-commercial-id="commercialId || ''"
			:property-region="region || ''"
			:property-type-name="propertyType?.name || ''"
			:property-id="propertyId || ''"
			:asking-price="askingPrice"
		/>
	</div>
</template>
